import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dashboard from '../services/DashboardService';
import { dashboard_slice_name, dashboard_thunks } from '../constants/dashboard';

const dashboardState = {
  applications: {
    results: [],
    loading: false,
    error: false,
    currentPage: 1,
    totalPages: 1,
    totalRecordCount: 0,
  },
  policyHolders: {
    results: [],
    loading: false,
    error: false,
    currentPage: 1,
    totalPages: 1,
    totalRecordCount: 0,
  },
  applicationFilters: {
    searchText: '',
    stages: [],
  },
  policyHolderFilters: {
    searchText: '',
    stages: [],
  },
};

export const initialData = dashboardState;

export const fetchLatestApplications = createAsyncThunk(
  dashboard_thunks.DASHBOARD_GET_LATEST_APPLICATIONS,
  async () => {
    const response = await dashboard.fetchLatestApplications();
    return response.data;
  }
);

export const fetchApplications = createAsyncThunk(
  dashboard_thunks.DASHBOARD_GET_APPLICATIONS,
  async (data, { rejectWithValue }) => {
    try {
      const response = await dashboard.fetchApplications(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchPolicyHolders = createAsyncThunk(
  dashboard_thunks.DASHBOARD_GET_POLICYHOLDERS,
  async (data, { rejectWithValue }) => {
    try {
      const response = await dashboard.fetchPolicyHolders(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const dashboardSlice = createSlice({
  name: dashboard_slice_name,
  initialState: initialData,
  reducers: {
    reset() {
      return initialData;
    },
    setApplicationFilters: (state, action) => {
      state.applicationFilters.searchText = action.payload.searchText;
      state.applicationFilters.stages = action.payload.stages;
    },
    setPolicyHolderFilters: (state, action) => {
      state.policyHolderFilters.searchText = action.payload.searchText;
      state.policyHolderFilters.stages = action.payload.stages;
    },
  },
  extraReducers: {
    [fetchLatestApplications.pending]: (state) => {
      state.applications.loading = true;
    },
    [fetchLatestApplications.rejected]: (state, action) => {
      state.applications.error = action.error.message;
      state.applications.loading = false;
    },
    [fetchLatestApplications.fulfilled]: (state, action) => {
      state.applications.data = action.payload.data;
      state.applications.loading = false;
    },
    [fetchApplications.pending]: (state) => {
      state.applications.loading = true;
      state.applications.error = false;
    },
    [fetchApplications.rejected]: (state) => {
      state.applications.error =
        'Sorry, we’re unable to show your applications at this moment. Please try again later.';
      state.applications.loading = false;
    },
    [fetchApplications.fulfilled]: (state, action) => {
      if (action.payload?.data?.results) {
        state.applications.results = action.payload.data.results;
        state.applications.currentPage = action.payload.data.currentPage;
        state.applications.totalPages = action.payload.data.totalPages;
        state.applications.totalRecordCount =
          action.payload.data.totalRecordCount;
        state.applications.loading = false;
      } else {
        state.applications.loading = false;
        state.applications.error =
          'Sorry, we’re unable to show your applications at this moment. Please try again later.';
      }
    },

    [fetchPolicyHolders.pending]: (state) => {
      state.policyHolders.loading = true;
      state.policyHolders.error = false;
    },
    [fetchPolicyHolders.rejected]: (state) => {
      state.policyHolders.error =
        'Sorry, we’re unable to show your policy holders at this moment. Please try again later.';
      state.policyHolders.loading = false;
    },
    [fetchPolicyHolders.fulfilled]: (state, action) => {
      if (action.payload?.data?.data) {
        state.policyHolders.results = action.payload.data.data.policyDetails;
        state.policyHolders.currentPage = action.payload.data.data.currentPage;
        state.policyHolders.totalPages = action.payload.data.data.totalPages;
        state.policyHolders.totalRecordCount =
          action.payload.data.data.totalRecordCount;
        state.policyHolders.loading = false;
      } else {
        state.policyHolders.loading = false;
        state.policyHolders.error =
          'Sorry, we’re unable to show your policy holders at this moment. Please try again later.';
      }
    },
  },
});

export const { reset, setApplicationFilters, setPolicyHolderFilters } =
  dashboardSlice.actions;

export default dashboardSlice;
